import Box from '@components/base/Box'
import Modal from '@components/base/Modal'
import { ArrayTextInput } from '@components/inputs/ArrayTextInput'
import { Button } from '@components/inputs/Button'
import { DropdownInput } from '@components/inputs/DropdownInput'
import { ItemPicker } from '@components/inputs/ItemPicker'
import { TextInput } from '@components/inputs/TextInput'
import {
  DiscountType,
  EventTicket,
  EventTicketDiscount,
  TicketType,
} from '@graphql/__generated/graphql'
import { useEventEditor } from '@libs/CreateEvent/useEventEditor'
import { useEffect, useState } from 'react'
import { MdOutlineDone, MdOutlineFileCopy } from 'react-icons/md'
import currencies from '@libs/currencies'

export type DiscountModalData = Partial<
  EventTicketDiscount & { editIndex?: number }
>

const domain = process.env.NEXT_PUBLIC_DOMAIN as string

export function DiscountLinkModal({
  show,
  setShow,
  tickets,
}: {
  show: boolean
  setShow: React.Dispatch<React.SetStateAction<boolean>>
  tickets?: (Omit<EventTicket, 'adminFee'> & { remainding?: number })[]
}) {
  const { formData, setFormData } = useEventEditor()
  const [linkData, setLinkData] = useState<{
    ticketName?: string
    discountCode?: string
    uri?: string
  }>({
    ticketName: undefined,
    discountCode: undefined,
    uri: undefined,
  })

  const [availableDiscounts, setAvailableDiscounts] = useState<
    EventTicketDiscount[]
  >([])

  const [copied, setCopied] = useState(false)

  const pageUrl = (dcu: string) =>
    `https://${domain.includes('qa') || domain.includes('app') ? '' : 'app.'}${domain}/event/${formData?.eventId}/register?dcu=${encodeURIComponent(dcu)}`

  useEffect(() => {
    if (linkData.ticketName && formData?.discounts) {
      const payload = formData.discounts.filter((f) => {
        const payload = f.tickets
          ?.map((m) => m.toUpperCase())
          .includes(linkData.ticketName!.toUpperCase())
        console.log({ f, payload, linkData })
        return payload
      })
      console.log({ discounts: payload })
      setAvailableDiscounts(payload)
    }
    if (linkData.ticketName && linkData.discountCode) {
      const payload = {
        name: linkData.ticketName,
        code: linkData.discountCode,
      }
      const encodedUriComp = Buffer.from(
        JSON.stringify(payload),
        'utf8'
      ).toString('base64')
      if (linkData.uri !== encodedUriComp)
        setLinkData((state) => ({ ...state, uri: encodedUriComp }))
    }
  }, [linkData])

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(pageUrl(linkData.uri!))
      setCopied(true)
      setTimeout(() => setCopied(false), 2000) // hide after 2s
    } catch (err) {
      console.error('Failed to copy!', err)
    }
  }

  const availableTickets = formData?.availableTickets?.tickets
  return (
    <Modal show={show} setShow={setShow} title={`Discount Links`}>
      <Box className={`flex flex-col justify-between items-center py-4`}>
        <Box
          className={`flex flex-row justify-evenly items-center w-full mb-4`}
        >
          {/* Tickets */}
          <DropdownInput
            label="Ticket"
            placeholder="Select a Ticket"
            options={
              availableTickets && availableTickets.length > 0
                ? availableTickets.map((t) => {
                    const isSoldOut = !!t.remaining
                      ? parseInt(t.remaining) < 1
                      : true
                    const payload = {
                      label: isSoldOut
                        ? `${t.ticketTitle!} - SOLD OUT`
                        : t.ticketTitle!,
                      value: t.ticketTitle!.toUpperCase(),
                      disabled: isSoldOut,
                    }
                    // console.log({ payload })
                    return payload
                  })
                : []
            }
            onChange={(val) => {
              setLinkData((state) => ({
                ...state,
                ticketName: val ?? state?.ticketName,
                discountPercentage: 0,
                discountCode: undefined,
              }))
            }}
            id={'ticket-dropdown'}
            selected={linkData?.ticketName ?? ''}
          />

          {/* Discounts */}
          <DropdownInput
            label="Discount"
            placeholder="Select a Discount"
            options={
              availableDiscounts && availableDiscounts.length > 0
                ? availableDiscounts.map((t) => {
                    const payload = {
                      label: t.title,
                      value: t.code,
                    }
                    console.log({ payload })
                    return payload
                  })
                : []
            }
            onChange={(val) => {
              setLinkData((state) => ({
                ...state,
                discountCode: val,
              }))
            }}
            id={'discount-dropdown'}
            selected={linkData?.discountCode ?? ''}
          />
        </Box>
        <Box
          show={!!linkData.uri}
          className={`flex flex-row w-full items-center`}
        >
          <TextInput
            label="Discount Link"
            className="w-full"
            disabled
            value={linkData.uri ? pageUrl(linkData.uri) : ''}
          />
          <div
            onClick={handleCopy}
            className="ml-3 bg-green-400 hover:bg-green-500 rounded-md p-2 cursor-pointer flex items-center justify-center transition-all duration-150"
            title="Copy password"
          >
            {copied ? (
              <MdOutlineDone className="text-white text-2xl" />
            ) : (
              <MdOutlineFileCopy className="text-white text-2xl" />
            )}
          </div>
          {/* {linkData.uri ? pageUrl(linkData.uri) : ''} */}
        </Box>
      </Box>
    </Modal>
  )
}

export default function DiscountModal({
  show,
  setShow,
  initialData,
  tickets,
}: {
  show: boolean
  setShow: React.Dispatch<React.SetStateAction<boolean>>
  initialData?: DiscountModalData
  tickets?: (Omit<EventTicket, 'adminFee'> & { remainding?: number })[]
}) {
  const { formData, setFormData } = useEventEditor()

  const [discountModalData, setDiscountModalData] = useState<
    DiscountModalData | undefined
  >(initialData)
  const [amountTouched, setAmountTouched] = useState(false)

  useEffect(() => {
    setDiscountModalData(initialData)
    setAmountTouched(false)
  }, [initialData])

  const amount = Number(discountModalData?.amount ?? 0)
  const selectedTicketPrices = (tickets ?? [])
    .filter((ticket) =>
      discountModalData?.tickets?.some(
        (title) => title.toUpperCase() === ticket.title.toUpperCase()
      )
    )
    .map((ticket) => Number(ticket.price))
  const maximumAmount =
    selectedTicketPrices.length > 0 ? Math.min(...selectedTicketPrices) : 0
  const amountError =
    discountModalData?.type === DiscountType.Amount && amountTouched
      ? amount <= 0
        ? 'Discount amount must be greater than 0'
        : !/^\d+(\.\d{1,2})?$/.test(String(discountModalData?.amount ?? ''))
          ? 'Discount amount must have at most 2 decimal places'
          : selectedTicketPrices.length > 0 && amount > maximumAmount
            ? `Discount amount cannot exceed the cheapest selected ticket (${maximumAmount.toFixed(2)})`
            : undefined
      : undefined
  const currencyCode = formData?.event?.currency?.toUpperCase() ?? 'GBP'
  const currencySymbol = currencies[currencyCode]?.symbol ?? currencyCode

  return (
    <Modal
      show={show}
      setShow={setShow}
      title={`${
        discountModalData?.editIndex !== undefined ||
        discountModalData?.editIndex === 0
          ? 'Edit'
          : 'Create'
      } Discount`}
    >
      <Box>
        <TextInput
          validations={{
            minLength: {
              value: 1,
              message: 'Discount name must be at least 1 character',
            },
            maxLength: {
              value: 70,
              message: 'Discount name must be at most 70 characters',
            },
          }}
          required
          label="Discount Title"
          placeholder="Early Bird Discount"
          value={discountModalData?.title ?? ''}
          onChange={(data) => {
            setAmountTouched(true)
            setDiscountModalData({
              ...discountModalData,
              title: data,
            })
          }}
        />
        <TextInput
          required
          label="Discount Quantity"
          placeholder="50"
          type="number"
          value={discountModalData?.quantity ?? 0}
          validations={{
            minLength: {
              value: 1,
              message: 'Discounts available must be at least 1 ',
            },
            maxLength: {
              value: 4,
              message: 'Discounts available must be at most 9999',
            },
          }}
          onChange={(data) => {
            setDiscountModalData({
              ...discountModalData,
              quantity: data,
            })
          }}
        />
        <ItemPicker
          itemClass="!w-1/2"
          choices={[
            { value: DiscountType.Percentage, title: 'Percentage' },
            {
              value: DiscountType.BuyOneGetOneFree,
              title: 'Buy 1 Get 1 Free',
            },
            { value: DiscountType.Amount, title: 'Amount' },
          ]}
          label="Discount Type"
          tooltip="Discount Type"
          onClick={(data) => {
            if (data === DiscountType.Amount) setAmountTouched(false)
            setDiscountModalData({
              ...discountModalData,
              type: data,
            })
          }}
          value={discountModalData?.type ?? DiscountType.Percentage}
        />
        <TextInput
          show={discountModalData?.type === DiscountType.Percentage}
          type="number"
          required
          label="Discount Percentage"
          value={discountModalData?.percentage ?? 0}
          onChange={(data) => {
            setDiscountModalData({
              ...discountModalData,
              percentage: data,
            })
          }}
        />
        <TextInput
          show={discountModalData?.type === DiscountType.Amount}
          type="number"
          required
          min={0.01}
          max={maximumAmount || undefined}
          label={`Discount Amount (${currencySymbol})`}
          value={discountModalData?.amount ?? ''}
          error={amountError}
          onChange={(data) => {
            setDiscountModalData({
              ...discountModalData,
              amount: data as any,
            })
          }}
        />

        <TextInput
          validations={{
            minLength: {
              value: 1,
              message: 'Discount codes must be at least 1 character',
            },
            maxLength: {
              value: 70,
              message: 'Discount codes must be at most 70 characters',
            },
          }}
          required
          label="Discount Code"
          placeholder="EARLYBIRD"
          value={discountModalData?.code ?? ''}
          onChange={(data) => {
            setDiscountModalData({
              ...discountModalData,
              code: data,
            })
          }}
        />
        <ArrayTextInput
          label="Applicable Tickets"
          placeholder={'Day Pass'}
          options={
            (tickets ?? [])
              .filter(
                (t) =>
                  t.ticketType === TicketType.Paid ||
                  t.ticketType === TicketType.GroupGuest ||
                  t.ticketType === TicketType.InvoiceTicket
              )
              .map((t) => ({
                id: t.title,
                label: t?.title,
              })) ?? []
          }
          value={discountModalData?.tickets?.map((t) => ({ key: t, value: t }))}
          onChange={(data) => {
            setDiscountModalData({
              ...discountModalData,
              tickets: data.map((t) => t.value) as any,
            })
          }}
        />
      </Box>
      <Box className="flex justify-between items-center w-full">
        {/* <Button */}
        {/*   className="my-4" */}
        {/*   title={'Back'} */}
        {/*   type="primary" */}
        {/* /> */}
        <Button
          className="my-4 ml-auto"
          title={`${
            discountModalData?.editIndex !== undefined ||
            discountModalData?.editIndex === 0
              ? 'Edit Discount'
              : 'Create Discount'
          }`}
          type="primary"
          onClick={(e) => {
            e.preventDefault()

            if (
              discountModalData?.type === DiscountType.Amount &&
              (amount <= 0 ||
                !/^\d+(\.\d{1,2})?$/.test(
                  String(discountModalData?.amount ?? '')
                ) ||
                (selectedTicketPrices.length > 0 && amount > maximumAmount))
            ) {
              setAmountTouched(true)
              return
            }

            const discountData: EventTicketDiscount = {
              title: discountModalData?.title ?? 'Discount',
              type: discountModalData?.type ?? DiscountType.Percentage,
              percentage: parseInt(
                discountModalData?.percentage?.toString() ?? '0'
              ),
              amount:
                discountModalData?.type === DiscountType.Amount
                  ? Number(Number(discountModalData.amount).toFixed(2))
                  : undefined,
              quantity: parseInt(
                discountModalData?.quantity?.toString() ?? '0'
              ),
              code: !!discountModalData?.code
                ? discountModalData.code
                : 'NEWCODE',
              tickets: discountModalData?.tickets ?? [],
            }

            const payload = [...(formData?.discounts ?? [])]

            if (
              discountModalData?.editIndex ||
              discountModalData?.editIndex === 0
            )
              payload[discountModalData.editIndex] = discountData
            else payload.push(discountData)

            setFormData({
              ...formData,
              discounts: payload,
            })

            setShow(false)
            setDiscountModalData(undefined)
            setAmountTouched(false)
          }}
        />
      </Box>
    </Modal>
  )
}
